import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CancelRounded, Check, NotInterested } from "@mui/icons-material";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_EditActionButtons,
  useMaterialReactTable,
} from "material-react-table";

import { HighchartsBase } from "@utilifeed/components";
import { SolidCard } from "@components";

import { SensorTableProps, SensorTableRowData } from "./types";
import { isEditableSensorType, useSensorTableData } from "./useSensorTableData";

type SensorEditDialogContentProps = {
  table: any;
  row: any;
  internalEditComponents: React.ReactNode;
  t: (key: string, options?: any) => string;
  editingEnabled: boolean;
  setEditingEnabled: (value: boolean) => void;
};

const SensorEditDialogContent: React.FC<SensorEditDialogContentProps> = ({
  table,
  row,
  internalEditComponents,
  t,
  editingEnabled,
  setEditingEnabled,
}) => {
  const theme = useTheme();

  React.useEffect(() => {
    if (row?.original) {
      setEditingEnabled(row.original.enabled !== false);
    }
  }, [row, setEditingEnabled]);

  const averageCalculations = editingEnabled ? "included" : "excluded";

  return (
    <>
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          ...theme.typography.titleMedium,
          textTransform: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {t("edit_sensor", { ns: "propai_properties" })}
        <IconButton
          aria-label={t("action_close", { ns: "_action" })}
          onClick={() => table.setEditingRow(null)}
          size="small"
        >
          <CancelRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: "10px",
            p: 2.5,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {internalEditComponents}

            <FormControl variant="standard" fullWidth sx={{ mt: 1 }}>
              <InputLabel
                id="average-calculations-label"
                shrink
                sx={{
                  color: theme.palette.text.secondary,
                  ...theme.typography.titleSmall,
                  "&.Mui-focused": {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {t("average_calculations", { ns: "propai_properties" })}
              </InputLabel>
              <Select
                labelId="average-calculations-label"
                value={averageCalculations}
                onChange={(e) => setEditingEnabled(e.target.value === "included")}
                renderValue={(value) => {
                  return value === "included"
                    ? t("included_in_average_calculations", { ns: "propai_properties" })
                    : t("excluded_from_average_calculations", { ns: "propai_properties" });
                }}
                sx={{
                  "& .MuiSelect-select": {
                    py: 1,
                    ...theme.typography.bodyMedium,
                    color: theme.palette.text.primary,
                  },
                  "&:before": { borderBottomColor: theme.palette.divider },
                  "&:hover:not(.Mui-disabled):before": { borderBottomColor: theme.palette.divider },
                  "& .MuiSvgIcon-root": {
                    color: theme.palette.text.secondary,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                      borderRadius: "12px",
                      boxShadow: theme.shadows[4],
                      maxWidth: "380px",
                    },
                  },
                }}
              >
                <MenuItem
                  value="included"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    whiteSpace: "normal",
                    p: 2,
                    borderRadius: "8px",
                    mx: 1,
                    my: 0.5,
                    backgroundColor:
                      averageCalculations === "included"
                        ? theme.palette.secondary.main
                        : "transparent",
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.secondary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.secondary.main,
                      },
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <Typography variant="titleSmall" sx={{ color: theme.palette.text.primary }}>
                    {t("included_in_average_calculations", { ns: "propai_properties" })}
                  </Typography>
                  <Typography
                    variant="bodySmall"
                    sx={{ color: theme.palette.text.secondary, mt: 1 }}
                  >
                    {t("included_desc", { ns: "propai_properties" })}
                  </Typography>
                </MenuItem>

                <MenuItem
                  value="excluded"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    whiteSpace: "normal",
                    p: 2,
                    borderRadius: "8px",
                    mx: 1,
                    my: 0.5,
                    backgroundColor:
                      averageCalculations === "excluded"
                        ? theme.palette.secondary.main
                        : "transparent",
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.secondary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.secondary.main,
                      },
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <Typography variant="titleSmall" sx={{ color: theme.palette.text.primary }}>
                    {t("excluded_from_average_calculations", { ns: "propai_properties" })}
                  </Typography>
                  <Typography
                    variant="bodySmall"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 1,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {t("excluded_desc", { ns: "propai_properties" })}
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <DialogActions
            sx={{
              px: 0,
              pt: 2.5,
              justifyContent: "flex-end",
              gap: 1.5,
              "& .MuiButton-text": {
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "999px",
                textTransform: "none",
                px: 3,
                py: 1,
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.primary.main,
              },
              "& .MuiButton-contained": {
                borderRadius: "999px",
                textTransform: "none",
                px: 3,
                py: 1,
                fontWeight: theme.typography.fontWeightBold,
              },
            }}
          >
            <MRT_EditActionButtons variant="text" table={table} row={row} />
          </DialogActions>
        </Box>
      </DialogContent>
    </>
  );
};

export const SensorTable: React.FC<SensorTableProps> = (props) => {
  const { t } = useTranslation(["propai", "_messages", "propai_properties", "_action"]);
  const theme = useTheme();
  const {
    title = t("all_sensors"),
    height = "100%",
    pagination = { pageSize: 10, showPagination: true },
    showChart = true,
    chartConfig,
    suppressTitle = false,
    editing,
  } = props;

  const {
    tableData,
    isLoading,
    rowSelection,
    setRowSelection,
    selectedSensorUids,
    chartSeries,
    error,
  } = useSensorTableData(props);

  const [editingEnabled, setEditingEnabled] = React.useState<boolean>(true);
  const [rowPatches, setRowPatches] = React.useState<Record<string, Partial<SensorTableRowData>>>(
    {}
  );

  const displayData = useMemo(
    () => tableData.map((row) => ({ ...row, ...rowPatches[row.uid] })),
    [tableData, rowPatches]
  );

  const mrtLocalization = useMemo(
    () => ({
      actions: t("text_actions", { ns: "_action" }),
      cancel: t("action_cancel", { ns: "_action" }),
      save: t("action_save", { ns: "_action" }),
    }),
    [t]
  );

  const columnDefinitions = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: t("external_id", { ns: "propai_properties" }),
        accessorKey: "external_id",
        enableEditing: false,
      },
      {
        header: t("sensor_type", { ns: "propai_properties" }),
        accessorKey: "sensor_type",
        enableEditing: false,
      },
      {
        header: t("apartment", { ns: "propai_properties" }),
        accessorKey: "apartment",
      },
      {
        header: t("floor", { ns: "propai_properties" }),
        accessorKey: "floor",
      },
      {
        header: t("street_address", { ns: "propai_properties" }),
        accessorKey: "street_address",
      },
      {
        header: t("average_calculations", { ns: "propai_properties" }),
        accessorKey: "average_calculations",
        enableEditing: false,
        Cell: ({ cell, row }) => {
          const label = cell.getValue<string>();
          if (!isEditableSensorType(row.original.sensor_type, editing)) return label;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {row.original.enabled !== false ? (
                <Check fontSize="small" sx={{ color: "text.secondary" }} />
              ) : (
                <NotInterested fontSize="small" sx={{ color: "text.secondary" }} />
              )}
              {label}
            </Box>
          );
        },
      },
    ],
    [t, editing]
  );

  // Create table instance - with row selection
  const table = useMaterialReactTable({
    columns: columnDefinitions,
    data: displayData,
    localization: mrtLocalization,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    enableEditing: (row) => !!editing && isEditableSensorType(row.original.sensor_type, editing),
    getRowId: (row, index) => {
      // Robust getRowId with fallback for Platform
      const uid = row.uid;
      if (typeof uid === "string" && uid.length > 0) {
        return uid;
      }
      // Fallback to index-based ID for invalid UIDs
      return `row-${index}`;
    },
    onEditingRowSave: async ({ row, values, table }) => {
      if (!editing) return;
      const sensorUid = row.original.uid as string;

      try {
        await editing.onSave({
          sensorUid,
          apartment: String(values.apartment ?? ""),
          enabled: editingEnabled,
          floor: Number(values.floor ?? 0),
          streetAddress: String(values.street_address ?? ""),
        });

        setRowPatches((prev) => ({
          ...prev,
          [sensorUid]: {
            apartment: String(values.apartment ?? ""),
            floor: String(values.floor ?? 0),
            street_address: String(values.street_address ?? ""),
            enabled: editingEnabled,
            average_calculations: editingEnabled
              ? t("included", { ns: "propai_properties" })
              : t("excluded", { ns: "propai_properties" }),
          },
        }));

        toast.success(t("text_sensor_metadata_updated", { ns: "propai_properties" }));

        table.setEditingRow(null);
      } catch (error) {
        toast.error(t("text_sensor_metadata_update_failed", { ns: "propai_properties" }));
      }
    },
    muiEditTextFieldProps: {
      variant: "standard",
      sx: {
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: theme.palette.text.disabled,
          color: theme.palette.text.disabled,
          opacity: 1,
        },
        "& .MuiInput-underline:before": { borderBottomColor: theme.palette.divider },
      },
    },
    muiTableContainerProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
      },
    },
    muiEditRowDialogProps: {
      PaperProps: {
        sx: {
          borderRadius: "14px",
          backgroundColor: theme.palette.background.default,
          width: 440,
          maxWidth: "95vw",
        },
      },
    },
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => {
      return (
        <SensorEditDialogContent
          table={table}
          row={row}
          internalEditComponents={internalEditComponents}
          t={t}
          editingEnabled={editingEnabled}
          setEditingEnabled={setEditingEnabled}
        />
      );
    },
    // Enable row selection functionality
    enableRowSelection: true,
    enableMultiRowSelection: props.selectionConfig?.enableMultiSelection !== false,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
    // Enable pagination with PropAI settings
    enablePagination: pagination.showPagination,
    initialState: {
      pagination: {
        pageSize: pagination.pageSize || 25,
        pageIndex: 0,
      },
    },
    enableGlobalFilter: true,
    enableFilters: true,
    enableSorting: true,
    muiTablePaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: "none",
        borderRadius: 2,
        p: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottomColor: theme.palette.divider,
        ...theme.typography.labelLarge,
        "& .MuiTableSortLabel-root, & .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Wrapper, & .Mui-TableHeadCell-Content-Labels":
          {
            ...theme.typography.labelLarge,
          },
        "& .MuiTableSortLabel-root": {
          color: theme.palette.text.primary,
          "&.Mui-active": {
            color: theme.palette.primary.main,
          },
        },
        "& .MuiTableSortLabel-icon": {
          color: `${theme.palette.primary.main} !important`,
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        borderBottomColor: theme.palette.divider,
        ...theme.typography.bodyMedium,
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        ...theme.typography.labelMedium,
      },
    },
    muiPaginationProps: {
      sx: {
        ...theme.typography.labelMedium,
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input, & .MuiTablePagination-actions, & .MuiSelect-select, & .MuiInputBase-input, & .MuiMenuItem-root, & .MuiPaginationItem-root, & .MuiTypography-root":
          {
            ...theme.typography.labelMedium,
          },
      },
    },
  });

  // Create Y-axes configuration for chart
  const yAxes = React.useMemo(() => {
    if (!chartConfig?.valueTypeMapping) return [];

    const axesMap = new Map();
    Object.values(chartConfig.valueTypeMapping).forEach((config) => {
      if (!axesMap.has(config.yAxis)) {
        axesMap.set(config.yAxis, {
          title: { text: config.title },
          opposite: config.yAxis === 1,
          gridLineWidth: 0,
        });
      }
    });

    return Array.from(axesMap.values());
  }, [chartConfig?.valueTypeMapping]);

  return (
    <Box sx={{ height, display: "flex", flexDirection: "column" }}>
      {!suppressTitle && title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: 400 }}>
        {showChart && selectedSensorUids.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("message_select_sensors_to_view_data_visualization", {
              ns: "_messages",
            })}
          </Alert>
        )}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading sensor data: {error.message || "Unknown error"}
          </Alert>
        ) : tableData.length === 0 && !isLoading ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No sensor data available. Check if sensors are properly configured.
          </Alert>
        ) : (
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <MaterialReactTable table={table} />
          </Box>
        )}
      </Box>

      {showChart && selectedSensorUids.length > 0 && (
        <Box sx={{ mt: 3, height: chartConfig?.height || 400 }}>
          <Typography variant="h6" gutterBottom>
            Sensor Data Chart ({selectedSensorUids.length} selected)
          </Typography>
          {chartSeries.length === 0 ? (
            <Alert severity="warning">
              No data available for the selected period. Try selecting a different date range or
              check if the sensors have recorded data.
            </Alert>
          ) : (
            <HighchartsBase
              chart={{ height: (chartConfig?.height || 400) - 50 }}
              series={chartSeries}
              xAxis={{
                type: "datetime",
              }}
              yAxis={yAxes}
              tooltip={{
                shared: true,
                crosshairs: true,
              }}
              legend={{
                enabled: true,
                layout: "horizontal",
                align: "center",
                verticalAlign: "bottom",
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

// Wrapper component that provides SolidCard styling (for PropAI compatibility)
export const SensorTableCard: React.FC<SensorTableProps & { cardTitle?: string }> = ({
  cardTitle,
  ...props
}) => {
  return (
    <SolidCard title={cardTitle || props.title}>
      <SensorTable {...props} suppressTitle />
    </SolidCard>
  );
};

export default SensorTable;
